import { useQuery } from '@tanstack/react-query'
import { performanceService } from '@/services/api/performanceService'

/**
 * Hook to fetch and cache the KPA framework from the backend
 * This ensures all components use the same KPA data and avoids hardcoded values
 * Uses the general KPA endpoint accessible by all users (not admin-only)
 */
export function useKPAFramework() {
  const query = useQuery({
    queryKey: ['kpa-framework'],
    queryFn: () => performanceService.getKPAs(),
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  })

  // KPAs are returned as a flat array - sort by sort_order to ensure consistent ordering
  // Order: 1. Operating Financial, 2. Internal Business, 3. Culture, 4. Customer and Stakeholder
  const kpas = [...(query.data || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))

  // Create a map for quick lookup by KPA name
  const kpaMap = new Map(kpas.map(kpa => [kpa.name, kpa]))

  // Create dropdown options for selectors (maintains sort_order)
  const kpaOptions = kpas.map(kpa => ({
    id: kpa.name,
    name: kpa.display_name || kpa.name,
    pillar: `Pillar ${kpa.strategic_pillar_id}`,
  }))

  return {
    // Raw data
    kpas,
    kpaMap,
    kpaOptions,
    
    // Query states
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    
    // Helper functions
    getKPAByName: (name: string) => kpaMap.get(name),
    getKPAsByPillar: (pillarId: string | number) => 
      kpas.filter(kpa => kpa.strategic_pillar_id === (typeof pillarId === 'string' ? parseInt(pillarId) : pillarId)),
  }
}
